import { isGeneId, isPhenotypeId } from 'src/util/util';
import { normalizeFetuses } from './pregnancyNormalize';

// Fetus diagnoses as FIRST-CLASS family diseases (Tim's decision 20).
//
// A fetus diagnosis is stored inside its pregnancy record (fetus-schema embeds the same
// `diagnosesSchema` a person uses), so it is invisible to the two family-level registries the render
// consults — `labelsSet.diseasesMap[id]` gates visibility (Canvas.jsx) and `diseaseColorMap[id]`
// supplies the fill (useCarrierStatus). Both are fed from the server's family diagnoses, which are
// gathered from MEMBERS. Without the helpers here a recorded fetus diagnosis would persist, reach the
// render node, and then silently fail to paint — and would be missing from the Legend that explains
// the colours.
//
// These are pure so the registry wiring is pinned by tests rather than only exercised through d3.

// Mirror of `fixedDiagnosesColors` in backend/util/util.js, which is the source of truth: the server
// assigns from this palette when a disease is first seen on a member. Mirrored (not imported —
// it is backend-only) so a colour picked for a fetus-only disease looks native alongside them.
export const FIXED_DIAGNOSES_COLORS = Object.freeze([
  '#e6194B',
  '#3cb44b',
  '#ffe119',
  '#4363d8',
  '#f58231',
  '#911eb4',
  '#42d4f4',
  '#f032e6',
  '#bfef45',
  '#fabed4',
  '#469990',
  '#dcbeff',
  '#9A6324',
  '#fffac8',
  '#800000',
  '#aaffc3',
  '#808000',
  '#ffd8b1',
  '#000075',
  '#a9a9a9'
]);

// Same conversion the backend palette generator uses (backend/util/util.js).
function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) =>
    Math.round(
      255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1))))
    )
      .toString(16)
      .padStart(2, '0');
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Every diagnosis recorded against any fetus of any pregnancy on these members, de-duplicated by
// disease id. `normalizeFetuses` is used rather than reading `fetuses` directly so a pregnancy written
// before the fetus sub-document existed still contributes its implicit fetus's diagnoses.
export function collectFetusDiagnoses(memberRecords) {
  const byId = new Map();
  (memberRecords || []).forEach((member) => {
    (member?.pregnancies || []).forEach((record) => {
      normalizeFetuses(record).forEach((fetus) => {
        (fetus?.diagnoses || []).forEach((dx) => {
          if (!dx || dx.id == null || byId.has(dx.id)) return;
          byId.set(dx.id, dx);
        });
      });
    });
  });
  return [...byId.values()];
}

// The disease ids a fetus diagnosis contributes — what the visibility map needs.
export function fetusDiagnosisIds(memberRecords) {
  return collectFetusDiagnoses(memberRecords).map((dx) => dx.id);
}

// The colour for a disease being recorded against a fetus.
//
// Rule one, and the one that matters clinically: the SAME condition must be the SAME colour across
// the family, so if any relative already carries this disease its colour wins — a mother and her
// fetus with the same condition must not shade differently. Rule two: no two DIFFERENT diseases may
// share a colour, so a disease new to the family takes the first palette colour no other disease is
// using — mirroring how the server allocates (backend/util/diagnosisColors.js). Counting distinct
// diseases into a palette index (the old scheme, server and client alike) handed the same index to
// different diseases whenever a disease repeated across members or was deleted.
export function pickDiagnosisColor(diseaseId, familyDiagnoses) {
  const list = (familyDiagnoses || []).filter(Boolean);
  const existing = list.find(
    (dx) => String(dx.id) === String(diseaseId) && dx.color
  );
  if (existing) return existing.color;
  const used = new Set(
    list
      .map((dx) =>
        typeof dx.color === 'string' ? dx.color.trim().toLowerCase() : ''
      )
      .filter((c) => c !== '')
  );
  const free = FIXED_DIAGNOSES_COLORS.find((c) => !used.has(c.toLowerCase()));
  if (free) return free;
  // Palette exhausted (>20 distinct family diseases): walk hues in golden-angle
  // steps — deterministic, and consecutive picks land far apart on the wheel,
  // so the first unused hue is also visually distinct from its neighbours.
  for (let i = 0; i < 360; i++) {
    const candidate = hslToHex((i * 137.508) % 360, 80, 55);
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  return FIXED_DIAGNOSES_COLORS[0];
}

// Build the stored diagnosis entry for a fetus from a disease-search result. Shape matches
// `diagnosesSchema` so it is the same record a person carries — which is what makes REQ-I5's
// carry-over on birth a plain array copy, and what lets the existing shading pass consume it.
//
// `displayOnPedigree` is true because the render filter requires it; `age` is deliberately omitted (a
// fetus has no age of diagnosis — gestation is the analogue and lives on the pregnancy); and
// `affectedStatus` is left unset, which the shading pass treats as affected (it only excludes an
// explicit 'unaffected'/'unknown').
export function buildFetusDiagnosis(disease, { familyDiagnoses, recordedBy }) {
  return {
    id: disease.id,
    name: disease.name,
    color: pickDiagnosisColor(disease.id, familyDiagnoses),
    displayOnPedigree: true,
    isPatientReported: false,
    clinicallyConfirmed: false,
    isPrivate: false,
    ...(recordedBy ? { recordedBy } : {})
  };
}

// Merge fetus diseases into a family disease list for the colour map, without disturbing the
// canonical entries: a disease already known to the family keeps its server-assigned colour.
export function withFetusDiseases(familyDiagnoses, memberRecords) {
  const known = new Set(
    (familyDiagnoses || [])
      .filter((dx) => dx && dx.id != null)
      .map((dx) => String(dx.id))
  );
  const extra = collectFetusDiagnoses(memberRecords).filter(
    (dx) => !known.has(String(dx.id))
  );
  return extra.length
    ? [...(familyDiagnoses || []), ...extra]
    : familyDiagnoses;
}

// === DYSMORPHIC FEATURES (REQ-E1) ==========================================================
// HPO terms on a fetus. Same story as diagnoses, one registry along: the phenotype LABEL pass gates on
// `labelsSet.phenotypesMap[id]` exactly as shading gates on `diseasesMap[id]`, and that map is fed from
// member data which cannot see inside a pregnancy record. A person's phenotypes are saved via
// POST /phenotypes, which requires a familyMemberId a fetus does not have — so, as with diagnoses, the
// fetus's live on the record and are merged in at the render boundary.

// The disease search returns diseases, genes and phenotypes mixed together, and the app routes them by
// id prefix. Sprint 96 (TGC-1586) added exactly these predicates to src/util/util.js, so they are
// re-exported from there rather than defined twice — two copies of a routing rule that decides whether
// a term becomes a DIAGNOSIS or a FEATURE is precisely the kind of thing that drifts. The guard against
// a non-string id stays here, since the shared versions use optional chaining and would return
// undefined rather than false.
export const isPhenotypeTermId = (id) =>
  typeof id === 'string' && isPhenotypeId(id);

export const isGeneTermId = (id) => typeof id === 'string' && isGeneId(id);

// Every phenotype recorded against any fetus, de-duplicated by term id.
export function collectFetusPhenotypes(memberRecords) {
  const byId = new Map();
  (memberRecords || []).forEach((member) => {
    (member?.pregnancies || []).forEach((record) => {
      normalizeFetuses(record).forEach((fetus) => {
        (fetus?.phenotypes || []).forEach((p) => {
          if (!p || p.id == null || byId.has(p.id)) return;
          byId.set(p.id, p);
        });
      });
    });
  });
  return [...byId.values()];
}

export function fetusPhenotypeIds(memberRecords) {
  return collectFetusPhenotypes(memberRecords).map((p) => p.id);
}

// The stored phenotype entry for a fetus, shaped like a person's (the `entitySchema` field set), so the
// existing label pass consumes it unchanged. `observed` defaults TRUE: recording a feature means it was
// seen — an explicitly-absent feature is a separate clinical statement the clinician sets deliberately.
export function buildFetusPhenotype(term, { recordedBy } = {}) {
  return {
    id: term.id,
    name: term.name,
    observed: true,
    displayOnPedigree: true,
    clinicallyConfirmed: false,
    patientReported: false,
    isPrivate: false,
    ...(recordedBy ? { recordedBy } : {})
  };
}
